import {connect} from 'react-redux';

import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {teammateCanAccessInbox} from '../../../helpers/modelHelpers';
import {AppState} from '../../../store/appState';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {dataUpdatedAction} from '../../../store/data/dataGroupsActions';
import {getFlatInboxes, getInboxes} from '../../../store/data/inboxes/inboxesSelectors';
import {getUser} from '../../../store/data/teammates/teammatesSelectors';
import {makeUserIsAdminForTeamNamespace} from '../../../store/data/teams/teamsSelectors';
import {LensFormMultiSelectInboxes} from './lensFormMultiSelectInboxes';

/*
 * Types.
 */

export interface LensFormInboxesMap {
  [key: number]: InboxModel;
}

/*
 * Props.
 */

interface LensFormMultiselectInboxesContainerProps {
  namespace: string | undefined;
  selectedIds: ReadonlyArray<number>;
  lensId: number | undefined;
  isEditingSharedView?: boolean;
}

/*
 * Selectors.
 */

function getNamespace(_state: AppState, props: LensFormMultiselectInboxesContainerProps) {
  return props.namespace;
}

function getSelectedIds(_state: AppState, props: LensFormMultiselectInboxesContainerProps) {
  return props.selectedIds;
}

function getIsEditingSharedView(_state: AppState, props: LensFormMultiselectInboxesContainerProps) {
  return props.isEditingSharedView ?? false;
}

function getLensId(_state: AppState, props: LensFormMultiselectInboxesContainerProps) {
  return props.lensId;
}

const getIsNamespaceAdmin = createSelector([getNamespace], (namespace) =>
  Boolean(namespace && makeUserIsAdminForTeamNamespace(namespace)),
);

const getShouldLoadAllInboxes = createSelector(
  [getIsNamespaceAdmin, getIsEditingSharedView],
  (isNamespaceAdmin, isEditingSharedView) => isNamespaceAdmin && isEditingSharedView,
);

/* Selects all user accessible inboxes for the given namespace */
const getUserAccessibleInboxes = createSelector(
  [getFlatInboxes, getUser, getNamespace],
  (inboxes, user, namespace) =>
    namespace
      ? inboxes.filter((inbox) => inbox.namespace === namespace && teammateCanAccessInbox(user, inbox))
      : [],
);

const getSelectedInboxesInState = createSelector([getInboxes, getSelectedIds], (inboxes, selectedIds) =>
  selectedIds.reduce<LensFormInboxesMap>((prev, inboxID) => {
    const selectedInbox = inboxes[inboxID];
    if (selectedInbox) {
      return {...prev, [inboxID]: selectedInbox};
    }

    return prev;
  }, {}),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<LensFormMultiselectInboxesContainerProps>()(() => ({
  selectedInboxesInState: getSelectedInboxesInState,
  shouldLoadAllInboxes: getShouldLoadAllInboxes,
  choices: getUserAccessibleInboxes,
  companyUrl: getCompanyUrl,
  lensId: getLensId,
}));

const mapDispatchToProps = makeMapDispatchToProps<LensFormMultiselectInboxesContainerProps>()(() => ({
  updateInboxesInState: (missingInboxes: ReadonlyArray<InboxModel>) =>
    dataUpdatedAction({inboxes: missingInboxes}),
}));

export const LensFormMultiSelectInboxesContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(LensFormMultiSelectInboxes);
